import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CurrencyPipe } from '@frontend/sports/common/core/feature/money';
import { NativePriceCalculatorService } from '@frontend/sports/odds/feature/native-price-calculator';
import { OddsFormatConverterService } from '@frontend/sports/odds/feature/native-price-odds-format-converter';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { BettingSettingsService, UserOddsFormatSettingsService, UserService } from '@frontend/sports/user/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { combineLatest, map } from 'rxjs';

import { OptionComponent } from '../option.component';

@Component({
    selector: 'ms-scorecast-footer',
    templateUrl: './scorecast-footer.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, OptionComponent, IconCustomComponent, CurrencyPipe],
})
export class ScorecastFooterComponent {
    private userService = inject(UserService);
    private oddsFormatConverter = inject(OddsFormatConverterService);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private sitecore = inject(Sitecore);
    private bettingSettingsService = inject(BettingSettingsService);

    private readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    activeEventOption = input<EventOption | undefined>();

    @Input() optionGroup: EventOptionGroup;
    @Input() event: EventModel;
    @Input() warningMessage: string;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';

    @HostBinding('class') className = 'scorecast-option-group-footer';

    readonly symbol = this.userService.currency;
    readonly showPossibleWinnings = this.marketGroupingConfig.scorecastPossibleWinningsVisible;
    readonly infoStakeReturnsText = this.sitecore.event.InfoStakeReturns || 'sitecore.event.InfoStakeReturns';

    defaultStake = toSignal(
        combineLatest([this.bettingSettingsService.bettingSettings$, this.bettingSettingsService.configSettings$]).pipe(
            map(([bettingSettings, configSettings]) => bettingSettings.defaultStake || configSettings.minimumStake),
        ),
        { initialValue: 0 },
    );

    possibleReturn = computed<number>(() => {
        if (!this.activeEventOption()?.nativePrice || !this.defaultStake()) {
            return 0;
        }

        const winnings = NativePriceCalculatorService.multiply(
            this.activeEventOption()!.nativePrice,
            this.defaultStake(),
            this.userOddsFormatSettingsService.oddsFormat,
        );

        return this.oddsFormatConverter.getOddsByOddsFormat(winnings).toNumber();
    });
}
